import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  onboardingService,
  OnboardingResponse,
  UpdateOnboardingPayload,
  ChecklistItem,
  ChecklistItemId,
} from '../services/onboardingService';
import { useAuth } from '../auth/useAuth';
import { skipWhenOffline } from '../offline';
import { trackOnboardingStepCompleted } from './analytics';

interface TourProgressUpdate {
  completed?: boolean;
  lastStepId?: string;
}

interface OnboardingContextValue {
  state: OnboardingResponse | null;
  isLoading: boolean;
  drawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  update: (payload: UpdateOnboardingPayload) => Promise<OnboardingResponse | void>;
  dismiss: () => Promise<void>;
  revive: () => Promise<void>;
  activeTourId: string | null;
  startTour: (tourId: string) => void;
  endTour: () => void;
  recordTourProgress: (tourId: string, progress: TourProgressUpdate) => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTourId, setActiveTourId] = useState<string | null>(null);

  // Scope the cache by user so switching accounts in the same browser
  // doesn't flash another user's checklist state while we refetch.
  const onboardingKey = useMemo(
    () => ['onboarding', 'state', user?.uid ?? 'anon'] as const,
    [user?.uid],
  );

  const { data, isLoading, refetch } = useQuery<OnboardingResponse>(
    onboardingKey,
    onboardingService.getState,
    {
      // skipWhenOffline: GET /onboarding isn't replicated — don't fire doomed
      // requests offline; cached pre-offline state keeps the pill read-only.
      enabled: isAuthenticated && !!user && skipWhenOffline(),
      staleTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
    },
  );

  const mutation = useMutation(onboardingService.updateState, {
    onSuccess: (next) => {
      queryClient.setQueryData(onboardingKey, next);
    },
    onError: () => {
      enqueueSnackbar('Could not update onboarding. Please try again.', {
        variant: 'error',
      });
    },
  });

  // Offline (flag-gated): skip the PATCH silently. Onboarding/tour progress is
  // cosmetic and recomputed from server state, so it simply re-syncs on the
  // next online session — queueing it isn't worth a sync op, and surfacing the
  // error snackbar would spam an otherwise fully-local tour replay.
  const mutateWhenOnline = useCallback(
    (payload: UpdateOnboardingPayload): Promise<OnboardingResponse | void> => {
      if (!skipWhenOffline()) return Promise.resolve();
      return mutation.mutateAsync(payload);
    },
    [mutation],
  );

  // Refetch whenever the drawer opens so the checklist reflects any work
  // the user did while the drawer was closed (e.g., adding a client).
  // (Manual refetch ignores `enabled`, so it needs its own offline guard.)
  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
    if (skipWhenOffline()) void refetch();
  }, [refetch]);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Also refetch on every client-side route change — onboarding items flip
  // based on side effects from nearly every page (adding a client, completing
  // a SOAP note, sending a discharge summary, etc.), so re-polling on nav
  // keeps the pill accurate without the user having to open the drawer.
  const { pathname } = useLocation();
  const mountedPath = React.useRef(pathname);
  React.useEffect(() => {
    if (mountedPath.current === pathname) {
      mountedPath.current = pathname;
      return;
    }
    mountedPath.current = pathname;
    if (isAuthenticated && !!user && skipWhenOffline()) void refetch();
  }, [pathname, isAuthenticated, user, refetch]);

  // Tracks which checklist IDs were just sent in a manuallyCompletedItems
  // payload so the diff effect below can label their completion as "dismissed"
  // (user clicked Not needed) vs "completed" (backend auto-detected).
  const pendingManualDismissalsRef = useRef<Set<ChecklistItemId>>(new Set());

  const update = useCallback(
    (payload: UpdateOnboardingPayload) => {
      // Offline: skip silently (see mutateWhenOnline) — checked BEFORE the
      // dismissal bookkeeping so an unsent "Not needed" can't mislabel a later
      // backend-detected completion as a manual dismissal.
      if (!skipWhenOffline()) return Promise.resolve();
      if (payload.manuallyCompletedItems?.length) {
        for (const id of payload.manuallyCompletedItems) {
          pendingManualDismissalsRef.current.add(id);
        }
      }
      return mutation.mutateAsync(payload);
    },
    [mutation],
  );

  // Fire onboarding_step_completed Amplitude events when checklist items
  // transition from incomplete to completed. Distinguishes manual dismissal
  // ("Not needed" click → pendingManualDismissalsRef has the id) from
  // backend-detected completion (refetched data shows new completions the
  // user didn't manually mark).
  const previousCompletedRef = useRef<Set<ChecklistItemId>>(new Set());
  const initializedForUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (!data || !user?.uid) return;
    const allItems: ChecklistItem[] = data.tiers.flatMap((tier) => tier.items);
    const currentlyCompleted = new Set(
      allItems.filter((item) => item.completed).map((item) => item.id),
    );

    // First time we see data for this user — seed the baseline without firing
    // events for items that were already completed from a previous session.
    if (initializedForUserRef.current !== user.uid) {
      previousCompletedRef.current = currentlyCompleted;
      initializedForUserRef.current = user.uid;
      pendingManualDismissalsRef.current = new Set();
      return;
    }

    const newlyCompleted = allItems.filter(
      (item) => item.completed && !previousCompletedRef.current.has(item.id),
    );
    for (const item of newlyCompleted) {
      const wasManual = pendingManualDismissalsRef.current.has(item.id);
      trackOnboardingStepCompleted(item, wasManual ? 'dismissed' : 'completed');
      pendingManualDismissalsRef.current.delete(item.id);
    }

    previousCompletedRef.current = currentlyCompleted;
  }, [data, user?.uid]);

  const dismiss = useCallback(async () => {
    await mutateWhenOnline({ dismissed: true });
    setDrawerOpen(false);
  }, [mutateWhenOnline]);

  const revive = useCallback(async () => {
    await mutateWhenOnline({ revived: true });
  }, [mutateWhenOnline]);

  // Tour controls — TourRunner subscribes to `activeTourId` and mounts Joyride
  // when set. Progress is persisted to User.onboardingState.tourProgress via
  // the existing PATCH endpoint; no new backend work needed.
  const startTour = useCallback(
    (tourId: string) => {
      setActiveTourId(tourId);
      void mutateWhenOnline({
        lastActiveTourId: tourId,
        tourProgress: { [tourId]: { startedAt: new Date().toISOString() } },
      }).catch(() => {
        // Cosmetic progress write — never block the (fully local) tour on it;
        // the mutation's onError snackbar already covers online failures.
      });
    },
    [mutateWhenOnline],
  );

  const endTour = useCallback(() => {
    setActiveTourId(null);
  }, []);

  const recordTourProgress = useCallback(
    async (tourId: string, progress: TourProgressUpdate) => {
      const existing = data?.state.tourProgress?.[tourId];
      const startedAt = existing?.startedAt ?? new Date().toISOString();
      const next = {
        [tourId]: {
          startedAt,
          ...(existing ?? {}),
          ...(progress.lastStepId ? { lastStepId: progress.lastStepId } : {}),
          ...(progress.completed ? { completedAt: new Date().toISOString() } : {}),
        },
      };
      await mutateWhenOnline({ tourProgress: next });
    },
    [data, mutateWhenOnline],
  );

  const value = useMemo<OnboardingContextValue>(
    () => ({
      state: data ?? null,
      isLoading,
      drawerOpen,
      openDrawer,
      closeDrawer,
      update,
      dismiss,
      revive,
      activeTourId,
      startTour,
      endTour,
      recordTourProgress,
    }),
    [
      data,
      isLoading,
      drawerOpen,
      openDrawer,
      closeDrawer,
      update,
      dismiss,
      revive,
      activeTourId,
      startTour,
      endTour,
      recordTourProgress,
    ],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = (): OnboardingContextValue => {
  const ctx = useContext(OnboardingContext);
  if (!ctx) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return ctx;
};
